import React, { useEffect, useState } from "react";
import { useAuth } from "../Context/AuthContext";
import "../Styling/LeaveDetailsScreen.css";
import { getLeavesData, changeLeaveStatus } from "../Api/leaveDetailsApi";

import { FiUsers, FiSearch } from "react-icons/fi";
import { User, IdCard, Building2 } from "lucide-react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const LeaveDetailsScreen = () => {
  const { user } = useAuth();

  const [leaveData, setLeaveData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [leaveTypeFilter, setLeaveTypeFilter] = useState("");
  const [startDateFilter, setStartDateFilter] = useState("");
  const [endDateFilter, setEndDateFilter] = useState("");
  const [companyFilter, setCompanyFilter] = useState("");

  const fetchLeaveData = async () => {
    try {
      const res = await getLeavesData(
        user?.userId,
        user?.organisationId,
        user?.token,
      );

      const data = res?.data || [];
      setLeaveData(data);
    } catch (err) {
      console.error("Error fetching leave data:", err);
    }
  };

  useEffect(() => {
    if (user?.userId && user?.organisationId) {
      fetchLeaveData();
    }
  }, [user]);

  const filteredData = leaveData.filter((item) => {
    const matchesSearch = item?.Resource_name?.toLowerCase().includes(
      searchTerm.toLowerCase(),
    );

    const matchesStatus =
      !statusFilter ||
      item?.leaveStatus?.toLowerCase() === statusFilter.toLowerCase();

    const matchesLeaveType =
      !leaveTypeFilter ||
      item?.Description?.toLowerCase() === leaveTypeFilter.toLowerCase();

    const leaveStart = item?.leaveStartDate
      ? item.leaveStartDate.split("T")[0]
      : "";

    const leaveEnd = item?.leaveEndDate ? item.leaveEndDate.split("T")[0] : "";

    const matchesStartDate = !startDateFilter || leaveStart >= startDateFilter;

    const matchesEndDate = !endDateFilter || leaveEnd <= endDateFilter;

    const matchesCompany =
      !companyFilter ||
      item?.Company_Name?.toLowerCase() === companyFilter.toLowerCase();

    return (
      matchesSearch &&
      matchesStatus &&
      matchesLeaveType &&
      matchesStartDate &&
      matchesEndDate &&
      matchesCompany
    );
  });

  const handleLeaveAction = async (item, activity) => {
    try {
      await changeLeaveStatus(item?.LeaveMasterNewGUID, activity, user?.userId);

      fetchLeaveData();

      alert(`Leave ${activity} successful`);
    } catch (err) {
      console.error(`${activity} failed`, err);

      alert(`Failed to ${activity} leave`);
    }
  };

  const downloadExcel = () => {
    const exportData = filteredData.map((item) => ({
      "Resource ID": item.ResourceId,
      "Employee Name": item.Resource_name,
      Status: item.leaveStatus,
      "Leave Type": item.Description,
      "Start Date": item.leaveStartDate
        ? new Date(item.leaveStartDate).toLocaleDateString()
        : "",
      "End Date": item.leaveEndDate
        ? new Date(item.leaveEndDate).toLocaleDateString()
        : "",
      Duration: `${item.leaveDuration}-${item.LeaveUnit}`,
      "Start Half": item.startHalf,
      "End Half": item.endHalf,
      Remarks: item.leaveRemarks,
      Company: item.Company_Name,
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);

    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, worksheet, "Leaves");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const file = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    saveAs(file, `Leave_Report_${new Date().toISOString().split("T")[0]}.xlsx`);
  };

  return (
    <div className="resource-dashboard-container">
      {/* Header */}
      <div className="resource-dashboard-header">
        <h2>Leave Details</h2>
      </div>

      {/* Summary Label */}
      <div
        style={{
          padding: "14px 24px 0 24px",
          fontSize: "10px",
          fontWeight: 700,
          color: "#9ca3af",
          textTransform: "uppercase",
          letterSpacing: "0.07em",
          background: "#ffffff",
        }}
      >
        Leave Summary
      </div>

      {/* Summary Cards */}
      <div className="resource-dashboard-summary">
        <div className="resource-card highlight">
          <div className="card-row">
            <FiUsers className="card-icon blue" />
            <div className="card-text">
              <p>Total Leaves</p>
              <h3>{leaveData.length}</h3>
            </div>
          </div>
        </div>

        <div className="resource-card accent">
          <div className="card-row">
            <User className="card-icon orange" />
            <div className="card-text">
              <p>Pending Leaves</p>
              <h3>
                {
                  leaveData.filter(
                    (x) => x?.leaveStatus?.toLowerCase() === "pending",
                  ).length
                }
              </h3>
            </div>
          </div>
        </div>

        <div className="resource-card">
          <div className="card-row">
            <IdCard className="card-icon purple" />
            <div className="card-text">
              <p>Approved Leaves</p>
              <h3>
                {
                  leaveData.filter(
                    (x) => x?.leaveStatus?.toLowerCase() === "approved",
                  ).length
                }
              </h3>
            </div>
          </div>
        </div>

        <div className="resource-card">
          <div className="card-row">
            <Building2 className="card-icon gray" />
            <div className="card-text">
              <p>Organisation Name</p>
              <h3>{leaveData[0]?.Organisation_Name || "N/A"}</h3>
            </div>
          </div>
        </div>
      </div>

      {/* Search */}
      {/* Search + Filters */}
      <div className="resource-search-bar leave-filter-bar">
        <div className="search-input-wrapper leave-search">
          <FiSearch className="search-icon" />

          <input
            type="text"
            placeholder="Search employee..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">All Status</option>
          <option value="Pending">Pending</option>
          <option value="Approved">Approved</option>
          <option value="Rejected">Rejected</option>
          <option value="Cancelled">Cancelled</option>
        </select>

        <select
          value={leaveTypeFilter}
          onChange={(e) => setLeaveTypeFilter(e.target.value)}
        >
          <option value="">All Leave Types</option>

          {[...new Set(leaveData.map((x) => x.Description))].map(
            (type, index) => (
              <option key={index} value={type}>
                {type}
              </option>
            ),
          )}
        </select>

        <select
          value={companyFilter}
          onChange={(e) => setCompanyFilter(e.target.value)}
        >
          <option value="">All Companies</option>

          {[...new Set(leaveData.map((x) => x.Company_Name))].map(
            (company, index) => (
              <option key={index} value={company}>
                {company}
              </option>
            ),
          )}
        </select>

        <input
          type="date"
          value={startDateFilter}
          onChange={(e) => setStartDateFilter(e.target.value)}
        />

        <input
          type="date"
          value={endDateFilter}
          onChange={(e) => setEndDateFilter(e.target.value)}
        />

        <button
          className="clear-filter-btn"
          onClick={() => {
            setStatusFilter("");
            setLeaveTypeFilter("");
            setStartDateFilter("");
            setEndDateFilter("");
            setSearchTerm("");
            setCompanyFilter("");
          }}
        >
          Clear
        </button>

        <button onClick={downloadExcel}>
          Download Excel
        </button>
      </div>

      {/* Table */}
      <div className="resource-dashboard-table-wrapper">
        <table className="resource-dashboard-table">
          <thead>
            <tr>
              <th>Resource ID</th>
              <th>Employee Name</th>
              <th>Action</th>
              <th>Status</th>
              <th>Leave Type</th>

              {/* <th>MAV User ID</th> */}

              <th>Start Date</th>
              <th>End Date</th>
              <th>Duration</th>
              <th>Start Half</th>
              <th>End Half</th>
              <th>Remarks</th>

              <th>Company</th>
            </tr>
          </thead>

          <tbody>
            {filteredData.length > 0 ? (
              filteredData.map((item, index) => (
                <tr key={index}>
                  <td>
                    <span className="resource-id-chip">{item?.ResourceId}</span>
                  </td>
                  <td className="name-cell">
                    <div className="name-content">
                      <span>{item?.Resource_name}</span>
                      <small>{item?.Description}</small>
                    </div>
                  </td>
                  <td>
                    {item?.leaveStatus?.toLowerCase() === "pending" ? (
                      <div className="leaveDetailsScreen-actionButtons">
                        <button
                          className="leaveDetailsScreen-approveBtn"
                          onClick={() => handleLeaveAction(item, "Approve")}
                        >
                          Approve
                        </button>

                        <button
                          className="leaveDetailsScreen-holdBtn"
                          onClick={() => handleLeaveAction(item, "Hold")}
                        >
                          Hold
                        </button>

                        <button
                          className="leaveDetailsScreen-cancelBtn"
                          onClick={() => handleLeaveAction(item, "Reject")}
                        >
                          Reject
                        </button>
                      </div>
                    ) : item?.leaveStatus?.toLowerCase() === "approved" ? (
                      <div className="leaveDetailsScreen-actionButtons">
                        <button
                          className="leaveDetailsScreen-cancelBtn"
                          onClick={() => handleLeaveAction(item, "Cancel")}
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      "-"
                    )}
                  </td>
                  <td>
                    <span
                      className={`status-badge

${
  item?.leaveStatus?.toLowerCase() === "pending"
    ? "status-overdue"
    : item?.leaveStatus?.toLowerCase() === "rejected"
      ? "status-rejected"
      : item?.leaveStatus?.toLowerCase() === "cancelled"
        ? "status-cancelled"
        : "status-paid"
}
`}
                    >
                      {item?.leaveStatus || "N/A"}
                    </span>
                  </td>
                  <td>{item?.Description}</td>

                  {/* <td>{item?.Mav_UserID}</td> */}

                  <td>
                    {item?.leaveStartDate
                      ? new Date(item.leaveStartDate).toLocaleDateString()
                      : "-"}
                  </td>

                  <td>
                    {item?.leaveEndDate
                      ? new Date(item.leaveEndDate).toLocaleDateString()
                      : "-"}
                  </td>

                  <td>
                    {item?.leaveDuration}-{item?.LeaveUnit}
                  </td>

                  <td>{item?.startHalf}</td>

                  <td>{item?.endHalf}</td>

                  <td>{item?.leaveRemarks}</td>

                  <td>{item?.Company_Name}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="14" className="no-data">
                  No Leave Data Found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default LeaveDetailsScreen;
